import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Leaf, Trophy, Recycle, ArrowRight } from 'lucide-react';
import { FaCheckCircle, FaSeedling } from 'react-icons/fa';
import { MdLandscape } from 'react-icons/md';
import PlantBackground from '../components/PlantBackground';
import './Home.css';

/* ── Animated Counter ── */
const Counter = ({ target, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        let start = 0;
        const duration = 2000;
        const step = target / (duration / 16);
        const timer = setInterval(() => {
          start += step;
          if (start >= target) { setCount(target); clearInterval(timer); }
          else setCount(Math.floor(start));
        }, 16);
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return <span ref={ref} className="counter-val">{count}{suffix}</span>;
};

/* ── Plant Card ── */
const PlantCard = ({ img, name, category, delay }) => (
  <div className="plant-card" style={{ animationDelay: `${delay}s` }}>
    <div className="plant-card-img-wrap">
      <img src={img} alt={name} className="plant-card-img" loading="lazy" />
      <div className="plant-card-badge">{category}</div>
    </div>
    <div className="plant-card-info">
      <h3 className="plant-card-name">{name}</h3>
    </div>
  </div>
);

/* ── Feature Card ── */
const FeatureCard = ({ icon, title, desc, delay }) => (
  <div className="feature-card" style={{ animationDelay: `${delay}s` }}>
    <div className="feature-icon">{icon}</div>
    <h3 className="feature-title">{title}</h3>
    <p className="feature-desc">{desc}</p>
  </div>
);

/* ── Testimonial ── */
const testimonials = [
  {
    name: 'Priya Joshi',
    role: 'Interior Designer',
    text: 'Nisarg Nursery provides the perfect combination of beautiful plants and professional service. Their Plant Library concept is unique and extremely convenient.',
    avatar: '🌿'
  },

  {
    name: 'Rohit Shah',
    role: 'Corporate Client',
    text: 'The indoor plants have completely enhanced our office atmosphere. Employees love the greenery and the maintenance support is excellent.',
    avatar: '🍀'
  },

  {
    name: 'Megha Patil',
    role: 'Homeowner',
    text: 'Healthy plants, timely service and expert guidance. Nisarg Nursery has become our trusted partner for all indoor and outdoor plant requirements.',
    avatar: '🌱'
  }
];

/* ═══════════════════ HOME PAGE ═══════════════════ */
const Home = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % testimonials.length);
    }, 4000);

    // Global scroll reveal
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.reveal, .reveal-left, .reveal-right').forEach(el => observer.observe(el));

    return () => { clearInterval(timer); observer.disconnect(); };
  }, []);

  return (
    <div className="page-wrapper home-page">

      {/* ════ HERO SECTION ════ */}
      <section className="hero-section">
        <div className="hero-orb hero-orb-1"></div>
        <div className="hero-orb hero-orb-2"></div>
        <div className="hero-grid"></div>
        <div className="hero-content">
          <div className="hero-left" style={{ animation: 'fadeInLeft 0.9s ease forwards' }}>
            <div className="hero-tag-minimal">
              MOST TRUSTED PLANT NURSERY SINCE 2022
            </div>
            <h1 className="hero-title">
              Transform Your Space With <br />
              <span className="gold-text">Plant Library</span> <br />
              <em>By Nisarg Nursery</em>
            </h1>
            <p className="hero-desc">
              Bring nature into your home, office, or commercial space with our unique Plant Library Service. Enjoy fresh indoor and outdoor plants, expert maintenance, and regular plant replacement for a greener, healthier environment.
            </p>
            <div className="hero-btns">
              <Link to="/services" className="btn-primary">
                <span>Explore Plant Services</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
              <Link to="/about" className="btn-outline">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M10 8l6 4-6 4V8z" fill="currentColor" />
                </svg>
                Our Story
              </Link>
            </div>
            <div className="hero-trust">
              <div className="trust-avatars">
                {['🧑', '👩', '🧔', '👩‍🦱'].map((e, i) => (
                  <div key={i} className="trust-avatar" style={{ zIndex: 4 - i }}>{e}</div>
                ))}
              </div>
              <div className="trust-text">
                <strong>2,000+</strong> happy customers
                <div className="trust-stars">★★★★★ <span>4.9/5</span></div>
              </div>
            </div>
          </div>

          <div className="hero-right" style={{ animation: 'fadeInRight 0.9s ease 0.2s forwards', opacity: 0 }}>
            <div className="hero-img-frame">
              <div className="hero-img-glow" />
              <div className="hero-glass-dome">
                <video
                  className="hero-video"
                  autoPlay
                  muted
                  loop
                  playsInline
                >
                  <source src="/images/video.mp4" type="video/mp4" />
                </video>
              </div>


              {/* Floating badges */}
              <div className="hero-badge badge-1">
                <span className="badge-emoji"><Leaf size={20} /></span>
                <div>
                  <div className="badge-val">500+</div>
                  <div className="badge-label">Plant Species</div>
                </div>
              </div>
              <div className="hero-badge badge-2">
                <span className="badge-emoji"><Trophy size={20} /></span>
                <div>
                  <div className="badge-val">Award</div>
                  <div className="badge-label">Winning Design</div>
                </div>
              </div>
              <div className="hero-badge badge-3">
                <span className="badge-emoji"><Recycle size={20} /></span>
                <div>
                  <div className="badge-val">100%</div>
                  <div className="badge-label">Eco Friendly</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="scroll-indicator">
          <div className="scroll-mouse">
            <div className="scroll-dot" />
          </div>
          <span>Scroll to explore</span>
        </div>
      </section>

      {/* ════ STATS SECTION ════ */}
      <section className="stats-section">
        <div className="stats-inner">
          {[
            { val: 3, suffix: '+', label: 'Years of Experience' },
            { val: 1000, suffix: '+', label: 'Happy Clients' },
            { val: 500, suffix: '+', label: 'Plant Varieties' },
            { val: 100, suffix: '%', label: 'Plant Care Support' },
          ].map(({ val, suffix, label }) => (
            <div key={label} className="stat-item">
              <div className="stat-num">
                <Counter target={val} suffix={suffix} />
              </div>
              <div className="stat-label">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ════ FEATURED PLANTS ════ */}
      <section className="plants-section">
        <div className="section-container">
          <div className="section-header reveal">
            <div className="section-tag">
              <Leaf size={14} />
              Sangli's Trusted Plant Nursery & Plant Library Service
            </div>
            <h2 className="section-title">
              Best <span className="gold-text">Indoor, Outdoor & Decorative </span>
            </h2>
            <p className="section-subtitle">
              Shop our handpicked, 100% organic plants delivered fresh and healthy straight to your doorstep. Low-maintenance and pet-friendly options available.
            </p>
          </div>

          <div className="plants-grid">
            <PlantCard img="/images/monstera.png" name="Monstera Deliciosa" category="Tropical" delay={0.1} />
            <PlantCard img="/images/fiddle.png" name="Fiddle Leaf Fig" category="Indoor" delay={0.2} />
            <PlantCard img="/images/palm.png" name="Areca Palm" category="Outdoor" delay={0.3} />
          </div>

          <div className="plants-cta">
            <Link to="/plants" className="btn-outline" style={{ display: 'inline-flex', marginTop: '16px' }} >
              Explore Collection
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* ════ WHY US ════ */}
      <section className="features-section">
        <div className="section-container">
          <div className="features-grid">
            <div className="features-left">
              <div className="section-tag"> Why We Are #1</div>
              <h2 className="section-title">
                The Best Plant <br />
                <span className="gold-text">Nursery Experience</span>
              </h2>
              <p className="section-subtitle">
                Nisarg Nursery offers a unique Plant Library Service designed for homes, offices and commercial spaces. We provide healthy plants, professional maintenance, plant replacement and customized greenery solutions that keep your surroundings fresh and attractive throughout the year.
              </p>
              <Link to="/about" className="btn-primary" style={{ display: 'inline-flex', marginTop: '16px' }}>
                <span>Learn About Us</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
            <div className="features-cards">
              <FeatureCard
                icon={<Leaf size={24} />} title="Customized Plant Solutions"
                desc="Tailored plant packages for homes, offices, hotels, restaurants and commercial spaces."
                delay={0.1}
              />
              <FeatureCard
                icon={<FaSeedling size={24} />} title="Regular Plant Replacement"
                desc="Fresh plants are replaced periodically to maintain a vibrant and attractive environment."
                delay={0.2}
              />
              <FeatureCard
                icon={<MdLandscape size={24} />} title="Pro Landscape Design"
                desc="Award-winning garden design, vertical gardens, and terrace landscaping."
                delay={0.3}
              />
              <FeatureCard
                icon={<Trophy size={24} />} title="30-Day Plant Warranty"
                desc="Your peace of mind guaranteed. Free replacement if your plant arrives unhealthy."
                delay={0.4}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ════ GARDEN SHOWCASE ════ */}
      <section className="showcase-section">
        <div className="showcase-container">
          <div className="showcase-img-wrap">
            <img src="/images/sc2.png" alt="Nisarg Nursery Garden Design" className="showcase-img" />
            <div className="showcase-img-overlay" />
            <div className="showcase-exp-badge">
              <div className="exp-num">3+</div>
              <div className="exp-text">Years of <br />Excellence</div>
            </div>
          </div>
          <div className="showcase-content">
            <div className="section-tag"> Professional Garden Designers</div>
            <h2 className="section-title">
              Expert Landscape <br />
              <span className="gold-text">Gardening Services</span>
            </h2>
            <p className="showcase-desc">
              Looking for top landscape designers? From lush apartment balcony gardens to large-scale corporate greening, we provide end-to-end plant maintenance (AMC) and setup.
            </p>
            <ul className="showcase-list">
              {[
                'Indoor & Outdoor Plant Solutions',
                'Office Plant Rental & Maintenance (AMC)',
                'Vertical Gardens & Rooftop Landscaping',
                'Corporate Gifting & Event Greenery',
              ].map(item => (
                <li key={item} className="showcase-list-item">
                  <span className="check-icon"><FaCheckCircle /></span>
                  {item}
                </li>
              ))}
            </ul>
            <Link to="/services" className="btn-primary" style={{ display: 'inline-flex', marginTop: '32px' }}>
              <span>See All Services</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* ════ TESTIMONIALS ════ */}
      <section className="testimonials-section">
        <div className="section-container">
          <div className="section-header">
            <div className="section-tag"> Testimonials</div>
            <h2 className="section-title">
              What Our <span className="gold-text">Clients Say</span>
            </h2>
          </div>

          <div className="testimonials-track">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className={`testimonial-card ${i === activeTestimonial ? 'active' : ''}`}
                onClick={() => setActiveTestimonial(i)}
              >
                <div className="testimonial-stars">★★★★★</div>
                <p className="testimonial-text">"{t.text}"</p>
                <div className="testimonial-author">
                  <div className="testimonial-avatar">{t.avatar}</div>
                  <div>
                    <div className="testimonial-name">{t.name}</div>
                    <div className="testimonial-role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="testimonial-dots">
            {testimonials.map((_, i) => (
              <button
                key={i}
                className={`t-dot ${i === activeTestimonial ? 'active' : ''}`}
                onClick={() => setActiveTestimonial(i)}
                aria-label={`Testimonial ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ════ CTA SECTION ════ */}
      <section className="cta-section">
        <div className="cta-inner">
          <div className="cta-glow" />
          <div className="section-tag" style={{ justifyContent: 'center' }}> Start Your Journey</div>
          <h2 className="cta-title">
            Ready to <span className="gold-text">Bring</span> Nature Closer?
          </h2>
          <p className="cta-desc">
            Get expert guidance and discover the perfect indoor and outdoor plant solutions for your home, office or commercial space.
          </p>
          <div className="cta-btns">
            <Link to="/contact" className="btn-primary">
              <span>Get Free Plant Consultation</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
            <a href="tel:+919876543210" className="btn-outline">
              📞 Call Us Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
